import { useQuery } from '@tanstack/react-query'

import { StorageAPI } from '@makyoapp/makyo-sdk'
import { Order } from '@makyoapp/makyo-sdk/api/api'
import { StorageFoldersAPI } from '@makyoapp/makyo-sdk/api/Storage'
import { FileCategory, FilesOrderBy, IFilePresenter } from '@makyoapp/makyo-sdk/models/Files'
import { PaginatedItems } from '@makyoapp/makyo-common'

import { queryKeys } from '@/lib/react-query'
import { useCurrentOrgContext } from '@/context/CurrentOrgContext'

/**
 * Get Org Storage
 *
 * @todo add sdk support for folderId params
 */
export const useGetStorage = (opts?: {
  params: {
    page: number
    search: string
    categories?: FileCategory[]
    folderId?: string
  }
  enabled?: boolean
}) => {
  const { currentOrg } = useCurrentOrgContext()
  const orgID = currentOrg?.id

  const params = { ...opts?.params }

  return useQuery({
    queryKey: [queryKeys.orgStorage, orgID, { ...params }],
    queryFn: async () => {
      let storage: PaginatedItems<IFilePresenter> = {
        items: [],
        totalCount: 0,
      }

      if (!orgID) return storage

      storage = await StorageAPI.ReqList(
        orgID,
        params.page,
        params.search,
        Order.Desc,
        FilesOrderBy.CreatedAt,
        params.categories ? [...params.categories] : [],
        '',
        '',
        params.folderId ?? ''
      ).then((res) => ({
        items: res._Data,
        totalCount: res._Rows === 0 ? res._Data.length : res._Rows,
      }))

      return storage
    },
    enabled: opts?.enabled ? !!orgID && opts.enabled : !!orgID,
  })
}

/**
 * Get Org Storage Metrics
 */
export const useGetStorageMetric = (opts?: { enabled?: boolean }) => {
  const { currentOrg } = useCurrentOrgContext()
  const orgID = currentOrg?.id

  return useQuery({
    queryKey: [queryKeys.orgStorageMetrics, orgID],
    queryFn: () => {
      if (!orgID) return
      return StorageAPI.ReqGetMetrics({
        _OrgID: orgID,
      }).then((metric) => {
        return {
          categories: {
            [FileCategory.Audio]: metric._Audio._Count,
            [FileCategory.Image]: metric._Image._Count,
            [FileCategory.Video]: metric._Video._Count,
            [FileCategory.Document]: metric._Document._Count,
            [FileCategory.Other]: metric._Others._Count,
            [FileCategory.Folder]: 0, // todo
          },
          size: {
            [FileCategory.Image]: metric._Image._Size,
            [FileCategory.Audio]: metric._Audio._Size,
            [FileCategory.Video]: metric._Video._Size,
            [FileCategory.Document]: metric._Document._Size,
            [FileCategory.Other]: metric._Others._Size,
            [FileCategory.Folder]: 0, // todo
          },
          used: metric._UsedSpace,
          total: metric._TotalSpace,
        }
      })
    },
    enabled: opts?.enabled ? !!orgID && opts.enabled : !!orgID,
  })
}

/**
 * Get Org Storage Folders within orgz
 */
export const useGetFolders = (folderID = '') => {
  const { currentOrg } = useCurrentOrgContext()
  const orgID = currentOrg?.id

  return useQuery({
    queryKey: [queryKeys.orgStorageFolders, orgID, folderID],
    queryFn: () => {
      if (!orgID) return
      return StorageFoldersAPI.ReqList(folderID, orgID)
    },
    enabled: !!orgID,
  })
}
